import React, { useContext, useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';

import { GlobalContext } from '@contexts';
import { FormError, HelpText } from '@Forms/fields.jsx';

import {
  defaultAutocompleteOptions,
  formattedAddressFieldName,
  loadGoogleMapsPlacesScript,
  parsePlaceToAddressFields,
} from './addressAutocompleteUtils.js';

// Override google places setting autocomplete=off, which chrome autofill
// ignores and overlays on top of the places dropdown.
const autocompleteObserver = new MutationObserver((mutationsList, observer) => {
  for (const mutation of mutationsList) {
    if (
      mutation.attributeName === 'autocomplete' &&
      mutation.target.getAttribute('autocomplete') === 'off'
    ) {
      observer.disconnect();
      mutation.target.setAttribute('autocomplete', 'new-password');
    }
  }
});

const AddressAutocompleteInput = ({
  autocompleteOptions,
  field,
  inputId,
  inputRef,
  placeholder,
  props,
}) => {
  const globals = useContext(GlobalContext);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    const apiKey = globals?.creds?.dashboard_client_google_maps_api?.api_key;
    const fieldEl = inputRef.current;

    if (!apiKey || !fieldEl || fieldEl.dataset.addressAutocompleteInit) {
      return;
    }

    let cancelled = false;

    const handleInput = (e) => {
      if (!e.target.value) {
        e.target.removeAttribute('data-autocomplete-chosen');
        field.onChange(null);
      }
    };

    const handleFormSubmit = () => {
      fieldEl.blur();
    };

    const initAutocomplete = async () => {
      try {
        await loadGoogleMapsPlacesScript(apiKey);
      } catch {
        return;
      }

      if (cancelled || !inputRef.current) {
        return;
      }

      fieldEl.dataset.addressAutocompleteInit = 'true';
      autocompleteObserver.observe(fieldEl, { attributes: true });

      if (fieldEl.value) {
        fieldEl.setAttribute('data-autocomplete-chosen', 'true');
      }

      const combinedOptions = {
        ...defaultAutocompleteOptions,
        ...(autocompleteOptions || {}),
      };

      const ac = new window.google.maps.places.Autocomplete(
        fieldEl,
        combinedOptions,
      );
      autocompleteRef.current = ac;

      const parentForm = fieldEl.closest('form');
      parentForm?.addEventListener('submit', handleFormSubmit);
      fieldEl.addEventListener('input', handleInput);

      ac.addListener('place_changed', async () => {
        const place = await ac.getPlace();
        const parsed = parsePlaceToAddressFields(place);

        if (!parsed) {
          fieldEl.value = '';
          field.onChange(null);
          fieldEl.focus();
          return;
        }

        fieldEl.value = parsed.formatted_address;
        fieldEl.setAttribute('data-autocomplete-chosen', 'true');
        field.onChange(parsed);
      });
    };

    initAutocomplete();

    return () => {
      cancelled = true;
      const el = inputRef.current;
      if (el) {
        el.removeEventListener('input', handleInput);
        el.closest('form')?.removeEventListener('submit', handleFormSubmit);
        delete el.dataset.addressAutocompleteInit;
      }
      autocompleteRef.current = null;
    };
  }, [
    autocompleteOptions,
    field.onChange,
    globals?.creds?.dashboard_client_google_maps_api?.api_key,
    inputRef,
  ]);

  return (
    <input
      type="search"
      id={inputId}
      placeholder={placeholder}
      autoComplete="new-password"
      defaultValue={field.value?.formatted_address ?? ''}
      ref={(el) => {
        field.ref(el);
        inputRef.current = el;
      }}
      onBlur={(e) => {
        field.onBlur();
        if (e.target.getAttribute('data-autocomplete-chosen')) {
          return;
        }
        e.target.value = '';
        field.onChange(null);
      }}
      {...props}
    />
  );
};

const AddressAutocompleteField = ({
  autocompleteOptions,
  className,
  control,
  helpText,
  label,
  name,
  placeholder = '',
  options = {},
  ...props
}) => {
  const inputRef = useRef(null);
  const inputId = formattedAddressFieldName(name);

  return (
    <Controller
      name={name}
      control={control}
      rules={options}
      render={({ field, fieldState }) => (
        <div
          className={
            `sb-form-item-formgroup sb-form-item-input ` +
            (fieldState.error ? 'sb-form-item-error' : '') +
            ' ' +
            (className ? className : '')
          }
        >
          {label && <label htmlFor={inputId}>{label}</label>}
          <div className="sb-form-item-content">
            <AddressAutocompleteInput
              autocompleteOptions={autocompleteOptions}
              field={field}
              inputId={inputId}
              inputRef={inputRef}
              placeholder={placeholder}
              props={{
                ...props,
                'aria-invalid': fieldState.error ? 'true' : 'false',
              }}
            />
            {helpText && <HelpText message={helpText} />}
            {fieldState.error && (
              <FormError message={fieldState.error.message} />
            )}
          </div>
        </div>
      )}
    />
  );
};

export default AddressAutocompleteField;
